import React from "react";
import { Link as ReactRouterLink } from "react-router-dom";
import {
    Box,
    Container,
    Heading,
    Text,
    Button,
    Link,
    VStack,
    SimpleGrid,
    Image,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet";

export default function HowItWorks() {
    return (
        <Box bg="offwhite.100" minH="100vh">
            <Helmet>
                <title>How it works: repository, fire, pack | BetaPersona</title>
                <meta name="description" content="Connect your publisher accounts. Fire assessments from one place. Store results with managed access. Generate business-ready packs derived from publisher scores and your framework." />
            </Helmet>

            <Container maxW="container.xl" py={{ base: 12, md: 20 }}>
                <VStack spacing={8} align="stretch">
                    <Box>
                        <Heading as="h1" size="2xl" mb={6}>
                            Connect. Fire. Store. Generate the pack.
                        </Heading>
                        <Text fontSize="xl" color="ink.500" mb={8}>
                            BetaPersona sits beside the publisher platforms. We do not host rival tests to Psytech, Hogan, SHL etc. We connect to your existing accounts, hold the file, apply your access policy, and produce a business-ready report derived from publisher scores and your competency framework.
                        </Text>
                        <Image
                            src="/images/how-it-works-8297077.jpeg"
                            alt="Professional reviewing documents. Photo: Mikhail Nilov, Pexels"
                            borderRadius="md"
                            w="full"
                            h="auto"
                            objectFit="cover"
                            aspectRatio="16/9"
                        />
                    </Box>

                    <Box
                        bg="white"
                        p={{ base: 8, md: 12 }}
                        borderRadius="md"
                        shadow="sm"
                    >
                        <Heading as="h2" size="lg" mb={8}>
                            Four stages
                        </Heading>

                        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={8}>
                            <VStack align="start" spacing={3}>
                                <Box
                                    w="full"
                                    h={2}
                                    bg="navy.500"
                                    borderRadius="full"
                                />
                                <Heading as="h3" size="md">
                                    1. Connect
                                </Heading>
                                <Text>
                                    Connect your publisher accounts e.g. Psytech. Hogan, SHL / Saville, Thomas, 360 tools, etc. sit as sources beside the store. We do not host rival tests to Psytech, Hogan, SHL etc.
                                </Text>
                            </VStack>

                            <VStack align="start" spacing={3}>
                                <Box
                                    w="full"
                                    h={2}
                                    bg="navy.500"
                                    borderRadius="full"
                                />
                                <Heading as="h3" size="md">
                                    2. Fire
                                </Heading>
                                <Text>
                                    Candidates log into BetaPersona to see the battery allocated to them, then follow a direct authorised link out to the publisher. Fire assessments from one login. No portal-hopping, no emailing links around, no pulling PDFs by hand.
                                </Text>
                            </VStack>

                            <VStack align="start" spacing={3}>
                                <Box
                                    w="full"
                                    h={2}
                                    bg="navy.500"
                                    borderRadius="full"
                                />
                                <Heading as="h3" size="md">
                                    3. Store
                                </Heading>
                                <Text>
                                    Psychometric, 360 and performance assessments in one repository with managed access. You own the person after shortlist.
                                </Text>
                            </VStack>

                            <VStack align="start" spacing={3}>
                                <Box
                                    w="full"
                                    h={2}
                                    bg="navy.500"
                                    borderRadius="full"
                                />
                                <Heading as="h3" size="md">
                                    4. Generate
                                </Heading>
                                <Text>
                                    Produce a pack derived from publisher reports and mapped to your competency framework. The HR or business user can use it without a psychometric or test-user qualification.
                                </Text>
                            </VStack>
                        </SimpleGrid>

                        <Box mt={10} p={6} bg="offwhite.100" borderRadius="md">
                            <Text fontSize="sm" fontStyle="italic">
                                <strong>Live: <Link href="https://www.psytech.com/" isExternal color="navy.500" fontWeight="600">Psytech</Link>.</strong> Other publishers labelled "source" until integrated.
                            </Text>
                        </Box>
                    </Box>

                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                        <Box bg="white" p={8} borderRadius="md" shadow="sm">
                            <Heading as="h3" size="md" mb={4}>
                                What stays with the publisher
                            </Heading>
                            <VStack align="start" spacing={2}>
                                <Text>• Publisher test libraries</Text>
                                <Text>• Publisher normative data</Text>
                                <Text>• Scale-level scoring</Text>
                                <Text>• Publisher copyright</Text>
                            </VStack>
                        </Box>

                        <Box bg="white" p={8} borderRadius="md" shadow="sm">
                            <Heading as="h3" size="md" mb={4}>
                                What you get
                            </Heading>
                            <VStack align="start" spacing={2}>
                                <Text>• One repository with access policy</Text>
                                <Text>• Live integrations (no portal-hopping)</Text>
                                <Text>• Packs mapped to your framework</Text>
                                <Text>• White-labelled client portal</Text>
                            </VStack>
                        </Box>
                    </SimpleGrid>

                    {/* Three packs */}
                    <Box>
                        <Heading as="h2" size="lg" mb={8}>
                            Three packs the engine produces (illustrative)
                        </Heading>
                        <VStack align="start" spacing={8}>
                            <Box bg="white" p={8} borderRadius="md" shadow="sm" w="full">
                                <Heading as="h3" size="md" mb={4}>
                                    1. Hiring-manager pack after an agency shortlist
                                </Heading>
                                <Text mb={2} fontWeight="600">Use: recruitment / selection</Text>
                                <Text mb={3}>
                                    Agency reduced the field. One document for first and second interviews.
                                </Text>
                                <Text mb={3}>
                                    Typical inputs: Hogan (HPI, and HDS where used), SHL OPQ or similar, Thomas, Psytech, etc.
                                </Text>
                                <Text mb={3}>
                                    HR or business user first; talent/OP/HRBP can still open publisher reports. Side-by-side, interview probes, no match percentage.
                                </Text>
                            </Box>
                            <Box bg="white" p={8} borderRadius="md" shadow="sm" w="full">
                                <Heading as="h3" size="md" mb={4}>
                                    2. Leadership selection pack
                                </Heading>
                                <Text mb={2} fontWeight="600">Use: recruitment / selection (senior or leadership)</Text>
                                <Text mb={3}>
                                    Typical inputs: Hogan HPI, HDS, MVPI plus ability (SHL Verify or equivalent), etc.
                                </Text>
                                <Text mb={3}>
                                    Panel + Head of Talent + OP. Strengths, pressure points, values alignment. No fabricated percentiles.
                                </Text>
                            </Box>
                            <Box bg="white" p={8} borderRadius="md" shadow="sm" w="full">
                                <Heading as="h3" size="md" mb={4}>
                                    3. Development pack (with 360)
                                </Heading>
                                <Text mb={2} fontWeight="600">Use: L&D / internal</Text>
                                <Text mb={4} fontWeight="600">360 belongs here</Text>
                                <Text mb={3}>
                                    Someone already in post. Typical inputs: a 360, performance ratings, psychometric scores already held (Hogan HPI, HDS, MVPI, etc). Do not feature MBTI.
                                </Text>
                                <Text>
                                    Individual + manager; L&D/HRBP. Self, others, and psychometric pattern. Development report, not a selection recommendation.
                                </Text>
                            </Box>
                        </VStack>
                    </Box>

                    <Box bg="navy.500" color="white" p={8} borderRadius="md">
                        <Heading as="h3" size="lg" mb={4}>
                            Demo-able in one sitting
                        </Heading>
                        <Text mb={6}>
                            One login. Fire the assessment. Store it. Generate the pack. We can walk you through the workflow in a working session. If that session shows merit, we can set up a free trial.
                        </Text>
                        <Link
                            as={ReactRouterLink}
                            to="/contact"
                            _hover={{ textDecoration: "none" }}
                        >
                            <Button
                                bg="accent.orange"
                                color="white"
                                size="lg"
                                _hover={{ bg: "rgba(248, 100, 30, 0.85)" }}
                            >
                                Talk to us
                            </Button>
                        </Link>
                        <Text mt={4} fontSize="sm">
                            A working session with talent, HRBP or in-house occupational psychology.
                        </Text>
                    </Box>
                </VStack>
            </Container>
        </Box>
    );
}
